<div class="bmt-shell">
  <div class="bmt-card">
    <div class="bmt-header">
      <h2>Body Mass Calorie Recalibrator + WHOOP Sync</h2>
      <button type="button" class="premium-gold-btn">
  Premium
</button>
      <p>
        Fixed workout: <strong>1 mile walked</strong> + <strong>1 mile run</strong>.
        You can also connect WHOOP to pull your body measurements and recalculate
        your real WHOOP workout calories using the body-composition modifier.
      </p>
    </div>

    <div class="bmt-whoop-panel">
      <div class="bmt-whoop-copy">
        <h3>WHOOP Connection</h3>
        <p>
          Connect WHOOP to import height/weight and recalculate recent WHOOP workout calories
          against your body-mass modifier.
        </p>
      </div>

      <div class="bmt-whoop-actions">
        <button
          type="button"
          class="bmt-btn bmt-btn-primary"
          (click)="connectWhoop()"
          [disabled]="whoopBusy"
        >
          {{ whoopBusy && !whoopConnected ? 'Connecting...' : 'Connect WHOOP' }}
        </button>

        <button
          type="button"
          class="bmt-btn bmt-btn-secondary"
          (click)="loadWhoopWorkoutData()"
          [disabled]="whoopBusy || !whoopConnected"
        >
          {{ whoopBusy && whoopConnected ? 'Refreshing...' : 'Refresh WHOOP Workouts' }}
        </button>

        <button
          type="button"
          class="bmt-btn bmt-btn-secondary"
          (click)="disconnectWhoop()"
          [disabled]="whoopBusy || !whoopConnected"
        >
          Disconnect WHOOP
        </button>
      </div>

      <div class="bmt-whoop-status" *ngIf="whoopStatusMessage">
        {{ whoopStatusMessage }}
      </div>

      <div class="bmt-error" *ngIf="whoopErrorMessage">
        {{ whoopErrorMessage }}
      </div>

      <div class="bmt-whoop-meta" *ngIf="whoopProfile || whoopBodyMeasurement">
        <div class="bmt-chip" *ngIf="whoopProfile">
          Connected as {{ whoopProfile.firstName }} {{ whoopProfile.lastName }} ({{ whoopProfile.email }})
        </div>

        <div class="bmt-chip" *ngIf="whoopBodyMeasurement">
          WHOOP body measurements synced:
          {{ oneDecimal(whoopBodyMeasurement.heightMeter) }} m /
          {{ oneDecimal(whoopBodyMeasurement.weightKilogram) }} kg
          <span *ngIf="whoopBodyMeasurement.maxHeartRate"> / Max HR {{ whoopBodyMeasurement.maxHeartRate }}</span>
        </div>
      </div>
    </div>

    <form class="bmt-form" (ngSubmit)="recalculate()">
      <div class="bmt-grid">
        <div class="bmt-field">
          <label for="heightFeet">Height (feet)</label>
          <input id="heightFeet" type="number" min="3" max="8" [(ngModel)]="heightFeet" name="heightFeet" />
        </div>

        <div class="bmt-field">
          <label for="heightInches">Height (inches)</label>
          <input id="heightInches" type="number" min="0" max="11" [(ngModel)]="heightInches" name="heightInches" />
        </div>

        <div class="bmt-field">
          <label for="weightLbs">Weight (lbs)</label>
          <input id="weightLbs" type="number" min="50" max="700" [(ngModel)]="weightLbs" name="weightLbs" />
        </div>

        <div class="bmt-field">
          <label for="age">Age</label>
          <input id="age" type="number" min="18" max="100" [(ngModel)]="age" name="age" />
        </div>

        <div class="bmt-field">
          <label for="gender">Gender</label>
          <select id="gender" [(ngModel)]="gender" name="gender">
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>

        <div class="bmt-field">
          <label for="bodyFatPercent">Body Fat % (optional)</label>
          <input
            id="bodyFatPercent"
            type="number"
            min="3"
            max="70"
            step="0.1"
            [(ngModel)]="bodyFatPercent"
            name="bodyFatPercent"
            placeholder="Leave blank to estimate"
          />
        </div>
      </div>

      <div class="bmt-note">
        If body fat % is left blank, the component estimates it from BMI, age, and gender.
        When WHOOP is connected, refreshing also recalculates recent WHOOP workouts with the same modifier.
      </div>

      <div class="bmt-actions">
        <button type="submit" class="bmt-btn bmt-btn-primary">Recalculate</button>
        <button type="button" class="bmt-btn bmt-btn-secondary" (click)="reset()">Reset</button>
      </div>
    </form>

    <div class="bmt-error" *ngIf="errorMessage">
      {{ errorMessage }}
    </div>

    <div class="bmt-results" *ngIf="results">
      <div class="bmt-section-title">Fixed Workout Estimate</div>

      <div class="bmt-summary-grid">
        <div class="bmt-stat">
          <span class="bmt-stat-label">BMI</span>
          <span class="bmt-stat-value">{{ oneDecimal(results.bmi) }}</span>
        </div>

        <div class="bmt-stat">
          <span class="bmt-stat-label">Body Fat %</span>
          <span class="bmt-stat-value">{{ oneDecimal(results.bodyFatPercent) }}</span>
          <small *ngIf="results.usedEstimatedBodyFat">estimated</small>
        </div>

        <div class="bmt-stat">
          <span class="bmt-stat-label">Lean Mass</span>
          <span class="bmt-stat-value">{{ oneDecimal(results.leanMassLbs) }} lbs</span>
        </div>

        <div class="bmt-stat">
          <span class="bmt-stat-label">Fat Mass</span>
          <span class="bmt-stat-value">{{ oneDecimal(results.fatMassLbs) }} lbs</span>
        </div>

        <div class="bmt-stat">
          <span class="bmt-stat-label">Recalibration Factor</span>
          <span class="bmt-stat-value">{{ oneDecimal(results.recalibrationFactor) }}x</span>
        </div>
      </div>

      <div class="bmt-activity-table">
        <div class="bmt-row bmt-row-head">
          <div>Activity</div>
          <div>Distance</div>
          <div>Standard</div>
          <div>Adjusted</div>
        </div>

        <div class="bmt-row">
          <div>Walk</div>
          <div>1 mile</div>
          <div>{{ round(results.walkCaloriesBase) }} cal</div>
          <div>{{ round(results.walkCaloriesAdjusted) }} cal</div>
        </div>

        <div class="bmt-row">
          <div>Run</div>
          <div>1 mile</div>
          <div>{{ round(results.runCaloriesBase) }} cal</div>
          <div>{{ round(results.runCaloriesAdjusted) }} cal</div>
        </div>

        <div class="bmt-row bmt-row-total">
          <div>Total</div>
          <div>2 miles</div>
          <div>{{ round(results.totalCaloriesBase) }} cal</div>
          <div>{{ round(results.totalCaloriesAdjusted) }} cal</div>
        </div>
      </div>

      <div class="bmt-footnote">
        “Standard” is the weight-based estimate. “Adjusted” applies the component’s body-composition recalibration.
      </div>
    </div>

    <div class="bmt-results" *ngIf="whoopConnected">
      <div class="bmt-section-title">WHOOP Workout Recalibration</div>

      <div class="bmt-whoop-summary-grid">
        <div class="bmt-stat">
          <span class="bmt-stat-label">Recent Workouts</span>
          <span class="bmt-stat-value">{{ whoopTotals.workoutCount }}</span>
        </div>

        <div class="bmt-stat">
          <span class="bmt-stat-label">WHOOP Standard Total</span>
          <span class="bmt-stat-value">{{ round(whoopTotals.standardCalories) }} cal</span>
        </div>

        <div class="bmt-stat">
          <span class="bmt-stat-label">Adjusted Total</span>
          <span class="bmt-stat-value">{{ round(whoopTotals.adjustedCalories) }} cal</span>
        </div>
      </div>

      <div class="bmt-activity-table" *ngIf="whoopWorkouts.length">
        <div class="bmt-row bmt-row-head bmt-row-whoop">
          <div>Workout</div>
          <div>Distance</div>
          <div>Strain</div>
          <div>WHOOP Standard</div>
          <div>Adjusted</div>
        </div>

        <div class="bmt-row bmt-row-whoop" *ngFor="let workout of whoopWorkouts">
          <div>
            <strong>{{ workout.sportName }}</strong>
            <div class="bmt-subtle">{{ workout.startedAt | date:'medium' }}</div>
          </div>
          <div>{{ oneDecimal(workout.distanceMiles) }} mi</div>
          <div>{{ oneDecimal(workout.strain) }}</div>
          <div>
            {{ round(workout.standardCalories) }} cal
            <div class="bmt-subtle" *ngIf="workout.usedWhoopKilojoules">WHOOP kJ source</div>
            <div class="bmt-subtle" *ngIf="!workout.usedWhoopKilojoules">fallback estimate</div>
          </div>
          <div>{{ round(workout.adjustedCalories) }} cal</div>
        </div>
      </div>

      <div class="bmt-footnote" *ngIf="whoopConnected">
        WHOOP rows use WHOOP workout energy as the baseline when available, then apply the same body-composition modifier.
      </div>
    </div>
  </div>
</div>